import { Download } from 'lucide-react';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { useAuth } from './AuthContext';
import { api } from '../services/authService';
import {
  DOWNLOAD_BTN_CLASS,
  buildFileDownloadHref,
  sortDownloadFileObjects,
} from '../services/fileDownload';
import { CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import Modal from '../components/ui/Modal';

const DownloadPollingContext = createContext(null);

function formatApiErrorDetail(detail) {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && e.msg != null ? e.msg : JSON.stringify(e))).join('\n');
  }
  if (typeof detail === 'object') return JSON.stringify(detail);
  return String(detail);
}

function formatFileSize(bytes) {
  if (bytes == null || Number.isNaN(bytes)) return '—';
  const n = Number(bytes);
  if (n < 1024) return `${n} B`;
  const kb = n / 1024;
  if (kb < 1024) return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
  const gb = mb / 1024;
  return `${gb < 10 ? gb.toFixed(2) : gb.toFixed(1)} GB`;
}

function s3ObjectFilename(key) {
  if (key == null || key === '') return '—';
  const i = key.lastIndexOf('/');
  return i >= 0 ? key.slice(i + 1) : key;
}

function isTerminalDownloadStatus(status) {
  const s = (status || '').toLowerCase();
  return s === 'complete' || s === 'completed' || s === 'failed';
}

const downloadHistoryRefreshListenersRef = new Set();

export function subscribeDownloadHistoryRefresh(listener) {
  downloadHistoryRefreshListenersRef.add(listener);
  return () => {
    downloadHistoryRefreshListenersRef.delete(listener);
  };
}

function notifyDownloadHistoryRefresh(kind) {
  downloadHistoryRefreshListenersRef.forEach((fn) => {
    try {
      fn(kind);
    } catch (e) {
      console.error('download history refresh listener error:', e);
    }
  });
}

export function DownloadPollingProvider({ children }) {
  const { user: currentUser } = useAuth();
  const [downloadPoll, setDownloadPoll] = useState(null);
  const autoModalOpenedForExecutionIdRef = useRef(null);

  const [filesModalOpen, setFilesModalOpen] = useState(false);
  const [filesModalMeta, setFilesModalMeta] = useState(null);
  const [filesList, setFilesList] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState('');

  useEffect(() => {
    if (!currentUser) {
      setDownloadPoll(null);
      autoModalOpenedForExecutionIdRef.current = null;
      setFilesModalOpen(false);
      setFilesModalMeta(null);
      setFilesList([]);
      setFilesError('');
    }
  }, [currentUser]);

  const { data: downloadStatus } = useQuery({
    queryKey: [
      'downloadPollingStatus',
      downloadPoll?.kind,
      downloadPoll?.executionId,
      downloadPoll?.organization,
    ],
    queryFn: async () => {
      const endpoint =
        downloadPoll.kind === 'email_list'
          ? '/api/v2/requested_email_list_status'
          : '/api/v2/requested_signals_status';
      const res = await api.get(endpoint, {
        params: {
          id: downloadPoll.executionId,
          organization: downloadPoll.organization,
        },
      });
      return res.data;
    },
    enabled: Boolean(
      currentUser && downloadPoll?.executionId && downloadPoll?.organization && downloadPoll?.kind
    ),
    refetchInterval: (query) => {
      const st = (query.state.data?.execution_status || '').toLowerCase();
      if (isTerminalDownloadStatus(st)) return false;
      return 30000;
    },
  });

  const loadFilesAndOpenModal = useCallback(async (kind, executionId, organization) => {
    setFilesModalOpen(true);
    notifyDownloadHistoryRefresh(kind);
    setFilesModalMeta({ kind, executionId, organization });
    setFilesList([]);
    setFilesError('');
    setFilesLoading(true);
    try {
      const url =
        kind === 'email_list' ? '/api/v2/email_list_files' : '/api/v2/signal_files';
      const res = await api.get(url, {
        params: { execution_id: executionId, organization },
      });
      setFilesList(sortDownloadFileObjects(res.data?.objects));
    } catch (e) {
      setFilesError(
        formatApiErrorDetail(e.response?.data?.detail) || e.message || 'Failed to list files'
      );
    } finally {
      setFilesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!downloadPoll || !downloadStatus?.execution_status) return;
    const st = downloadStatus.execution_status.toLowerCase();
    if (!isTerminalDownloadStatus(downloadStatus.execution_status)) return;
    if (autoModalOpenedForExecutionIdRef.current === downloadPoll.executionId) return;
    autoModalOpenedForExecutionIdRef.current = downloadPoll.executionId;

    const { kind, executionId, organization } = downloadPoll;
    setDownloadPoll(null);

    if (st === 'failed') {
      toast.error(
        kind === 'email_list'
          ? 'Email list export failed or timed out. Check download history for details.'
          : 'Signal download failed or timed out. Check download history for details.'
      );
      return;
    }

    void loadFilesAndOpenModal(kind, executionId, organization);
  }, [downloadPoll, downloadStatus?.execution_status, loadFilesAndOpenModal]);

  const startEmailListExportPolling = useCallback((executionId, organization) => {
    if (!executionId || !organization) return;
    autoModalOpenedForExecutionIdRef.current = null;
    setDownloadPoll({ kind: 'email_list', executionId, organization });
  }, []);

  const startSignalExportPolling = useCallback((executionId, organization) => {
    if (!executionId || !organization) return;
    autoModalOpenedForExecutionIdRef.current = null;
    setDownloadPoll({ kind: 'signals', executionId, organization });
  }, []);

  const closeFilesModal = useCallback(() => {
    setFilesModalOpen(false);
    setFilesModalMeta(null);
    setFilesList([]);
    setFilesError('');
  }, []);

  const modalTitle =
    filesModalMeta?.kind === 'email_list' ? 'Email list files' : 'Signal download files';

  const value = useMemo(
    () => ({
      startEmailListExportPolling,
      startSignalExportPolling,
    }),
    [startEmailListExportPolling, startSignalExportPolling]
  );

  return (
    <DownloadPollingContext.Provider value={value}>
      {children}
      <Modal
        isOpen={filesModalOpen}
        onClose={closeFilesModal}
        className="w-full max-w-4xl max-h-[85vh] flex flex-col"
        zIndex="z-[100]"
      >
            <CardHeader className="pb-2 border-b pr-10">
              <CardTitle className="text-lg text-gray-900">{modalTitle}</CardTitle>
            </CardHeader>
            <CardContent className="overflow-y-auto flex-1 min-h-0 pt-4 space-y-3">
              {filesModalMeta && (
                <p className="text-sm text-gray-600">
                  <span className="font-medium text-gray-800">Execution ID:</span>{' '}
                  <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded break-all">
                    {filesModalMeta.executionId}
                  </code>
                </p>
              )}
              {filesLoading && <p className="text-sm text-gray-500">Loading objects…</p>}
              {filesError && (
                <p className="text-sm text-red-700 whitespace-pre-line">{filesError}</p>
              )}
              {!filesLoading && !filesError && filesList.length === 0 && (
                <p className="text-sm text-gray-500">
                  No <code className="text-xs">.csv.gz</code> files found under this prefix.
                </p>
              )}
              {!filesLoading && filesList.length > 0 && (
                <div className="overflow-x-auto border border-gray-200 rounded-md">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-700 border-b whitespace-nowrap min-w-[22rem]">
                          Filename
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700 border-b whitespace-nowrap">
                          Size
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700 border-b whitespace-nowrap">
                          Modified
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700 border-b min-w-[5rem]">
                          {' '}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filesList.map((obj) => (
                        <tr key={obj.key} className="border-b border-gray-100">
                          <td className="px-3 py-2 text-gray-800 font-mono text-xs whitespace-nowrap min-w-[22rem]">
                            {s3ObjectFilename(obj.key)}
                          </td>
                          <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                            {formatFileSize(obj.size)}
                          </td>
                          <td className="px-3 py-2 text-gray-700 whitespace-nowrap text-xs">
                            {obj.last_modified
                              ? new Date(obj.last_modified).toLocaleString()
                              : '—'}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <a
                              href={
                                filesModalMeta
                                  ? buildFileDownloadHref(
                                      filesModalMeta.kind === 'email_list'
                                        ? '/api/v2/requested_email_list_download'
                                        : '/api/v2/requested_signals_download',
                                      {
                                        id: filesModalMeta.executionId,
                                        organization: filesModalMeta.organization,
                                        filename: s3ObjectFilename(obj.key),
                                      }
                                    )
                                  : '#'
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              className={DOWNLOAD_BTN_CLASS}
                              aria-label="Download"
                              title="Download"
                            >
                              <Download className="h-4 w-4" aria-hidden />
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
      </Modal>
    </DownloadPollingContext.Provider>
  );
}

export function useDownloadPolling() {
  const ctx = useContext(DownloadPollingContext);
  if (!ctx) {
    throw new Error('useDownloadPolling must be used within DownloadPollingProvider');
  }
  return ctx;
}
